import { useMemo } from 'react';
import { CssBaseline, GlobalStyles, ThemeProvider as MuiThemeProvider } from '@/ui/primitives';
import { createAppTheme, type ThemeMode } from './createTheme';
import { colorTokenModes, r, s, surfaceTokenModes } from './tokens';
import type { DensityMode } from './tokens';
import type { PropsWithChildren } from 'react';

type ThemeProviderProps = PropsWithChildren<{ mode?: ThemeMode; density?: DensityMode }>;

export const ThemeProvider = ({
  children,
  mode = 'light',
  density = 'standard',
}: ThemeProviderProps): JSX.Element => {
  const theme = useMemo(() => createAppTheme(mode, density), [mode, density]);
  const colorSet = colorTokenModes[mode];
  const surfaceSet = surfaceTokenModes[mode];

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles
        styles={{
          ':root': {
            '--font-family-heading': '"Roobert", sans-serif',
            '--font-family-body': '"Neue Montreal", sans-serif',
            '--font-family-mono': '"Neue Montreal Mono", monospace',
            '--color-green': colorSet.green,
            '--color-red': colorSet.red,
            '--color-orange': colorSet.orange,
            '--color-brown': colorSet.brown,
            '--color-purple': colorSet.purple,
            '--color-blue': colorSet.blue,
            '--color-teal': colorSet.teal,
            '--color-pink': colorSet.pink,
            '--color-neutral': colorSet.neutral,
            '--surface-page': surfaceSet.page,
            '--surface-page-alt': surfaceSet.pageAlt,
            '--surface-page-muted': surfaceSet.pageMuted,
            '--surface-paper': surfaceSet.paper,
            '--surface-border': surfaceSet.border,
          },
          html: {
            backgroundColor: surfaceSet.page,
            minHeight: '100%',
            overflowX: 'auto',
            overflowY: 'auto',
          },
          body: {
            backgroundColor: surfaceSet.page,
            color: theme.app.colors.text,
            scrollbarGutter: 'auto',
            overflowX: 'auto',
            overflowY: 'auto',
            width: '100%',
            minHeight: '100%',
          },
          '#root': {
            backgroundColor: surfaceSet.page,
            width: '100%',
            overflowX: 'visible',
            minHeight: '100%',
          },
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: `${theme.app.colors.border} ${surfaceSet.page}`,
          },
          '*::-webkit-scrollbar': {
            width: '12px',
            height: '12px',
            backgroundColor: surfaceSet.page,
          },
          '*::-webkit-scrollbar-track': {
            backgroundColor: surfaceSet.page,
          },
          '*::-webkit-scrollbar-corner': {
            backgroundColor: surfaceSet.page,
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: theme.app.colors.border,
            borderRadius: r('pill'),
            border: `2px solid transparent`,
            backgroundClip: 'content-box',
          },
          '*::-webkit-scrollbar-thumb:hover': {
            backgroundColor: theme.palette.text.secondary,
          },
          a: { color: theme.app.colors.primary },
          '@keyframes flow-fade-in': { from: { opacity: 0 }, to: { opacity: 1 } },
          '@keyframes flow-fade-up': {
            from: { opacity: 0, transform: `translateY(${s('sm')})` },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
          '@keyframes flow-scale-in': {
            from: { opacity: 0, transform: 'scale(0.98)' },
            to: { opacity: 1, transform: 'scale(1)' },
          },
          '@keyframes flow-slide-standard': {
            from: { transform: 'translateX(0%)' },
            to: { transform: 'translateX(150%)' },
          },
          '@keyframes flow-slide-emphasized': {
            from: { transform: 'translateX(0%)' },
            to: { transform: 'translateX(150%)' },
          },
          '@keyframes flow-slide-linear': {
            from: { transform: 'translateX(0%)' },
            to: { transform: 'translateX(150%)' },
          },
          '.flow-page-enter': {
            animation: `flow-fade-up ${theme.app.motion.durations.slow} ${theme.app.motion.easing.standard}`,
          },
          '.flow-section-enter': {
            animation: `flow-fade-in ${theme.app.motion.durations.medium} ${theme.app.motion.easing.standard}`,
          },
          '.landing-page .flow-section-enter': {
            animation: `flow-fade-in ${theme.app.motion.durations.slow} ${theme.app.motion.easing.standard}`,
          },
          '.flow-card-enter': {
            animation: `flow-scale-in 80ms ${theme.app.motion.easing.standard}`,
          },
          '.flow-skip-link': {
            position: 'absolute',
            top: 0,
            left: 0,
            transform: 'translateY(-120%)',
            backgroundColor: theme.app.colors.surface,
            color: theme.app.colors.text,
            padding: `${s('xs')} ${s('sm')}`,
            border: `1px solid ${theme.app.colors.border}`,
            zIndex: theme.app.zIndex.tooltip,
            transition: `transform ${theme.app.motion.durations.fast} ${theme.app.motion.easing.standard}`,
          },
          '.flow-skip-link:focus': {
            transform: 'translateY(0)',
            outline: `2px solid ${theme.app.state.focus}`,
            outlineOffset: 2,
          },
          '.MuiPopover-paper, .MuiMenu-paper, .MuiPopover-paper.MuiPaper-rounded, .MuiMenu-paper.MuiPaper-rounded':
            {
              borderRadius: `${r('md')} !important`,
              overflow: 'hidden',
            },
          '.MuiMenu-list, .MuiAutocomplete-listbox, .MuiPopover-paper, .MuiMenu-paper, .MuiTooltip-tooltip, .MuiPickersLayout-root, .MuiPickersPopper-paper':
            {
              scrollbarWidth: 'thin',
              scrollbarColor: `${theme.app.colors.border} transparent`,
            },
          '.MuiMenu-list::-webkit-scrollbar, .MuiAutocomplete-listbox::-webkit-scrollbar, .MuiPopover-paper::-webkit-scrollbar, .MuiMenu-paper::-webkit-scrollbar, .MuiTooltip-tooltip::-webkit-scrollbar, .MuiPickersLayout-root::-webkit-scrollbar, .MuiPickersPopper-paper::-webkit-scrollbar':
            {
              width: s('sm'),
              height: s('sm'),
              backgroundColor: 'transparent',
            },
          '.MuiMenu-list::-webkit-scrollbar-track, .MuiAutocomplete-listbox::-webkit-scrollbar-track, .MuiPopover-paper::-webkit-scrollbar-track, .MuiMenu-paper::-webkit-scrollbar-track, .MuiTooltip-tooltip::-webkit-scrollbar-track, .MuiPickersLayout-root::-webkit-scrollbar-track, .MuiPickersPopper-paper::-webkit-scrollbar-track':
            {
              backgroundColor: 'transparent',
              borderRadius: r('pill'),
            },
          '.MuiMenu-list::-webkit-scrollbar-corner, .MuiAutocomplete-listbox::-webkit-scrollbar-corner, .MuiPopover-paper::-webkit-scrollbar-corner, .MuiMenu-paper::-webkit-scrollbar-corner, .MuiTooltip-tooltip::-webkit-scrollbar-corner, .MuiPickersLayout-root::-webkit-scrollbar-corner, .MuiPickersPopper-paper::-webkit-scrollbar-corner':
            {
              backgroundColor: 'transparent',
            },
          '.MuiMenu-list::-webkit-scrollbar-thumb, .MuiAutocomplete-listbox::-webkit-scrollbar-thumb, .MuiPopover-paper::-webkit-scrollbar-thumb, .MuiMenu-paper::-webkit-scrollbar-thumb, .MuiTooltip-tooltip::-webkit-scrollbar-thumb, .MuiPickersLayout-root::-webkit-scrollbar-thumb, .MuiPickersPopper-paper::-webkit-scrollbar-thumb':
            {
              backgroundColor: theme.app.colors.border,
              backgroundClip: 'content-box',
              border: `${s('xxs')} solid transparent`,
              borderRadius: r('pill'),
            },
          '.MuiMenu-list::-webkit-scrollbar-thumb:hover, .MuiAutocomplete-listbox::-webkit-scrollbar-thumb:hover, .MuiPopover-paper::-webkit-scrollbar-thumb:hover, .MuiMenu-paper::-webkit-scrollbar-thumb:hover, .MuiTooltip-tooltip::-webkit-scrollbar-thumb:hover, .MuiPickersLayout-root::-webkit-scrollbar-thumb:hover, .MuiPickersPopper-paper::-webkit-scrollbar-thumb:hover':
            {
              backgroundColor: theme.palette.text.secondary,
            },
          '.MuiMenu-paper:has(.MuiTablePagination-menuItem), .MuiMenu-paper.MuiPaper-rounded:has(.MuiTablePagination-menuItem), .MuiPopover-paper:has(.MuiTablePagination-menuItem), .MuiPopover-paper.MuiPaper-rounded:has(.MuiTablePagination-menuItem)':
            {
              borderRadius: `${r('md')} !important`,
              overflow: 'hidden',
            },
          '@media (max-width: 600px)': {
            '#termly-code-snippet-support .t-consentPrompt': {
              width: `calc(100vw - (${s('md')} * 2)) !important`,
              maxWidth: `calc(100vw - (${s('md')} * 2)) !important`,
              maxHeight: '36svh !important',
              left: `${s('md')} !important`,
              right: `${s('md')} !important`,
              bottom: `${s('md')} !important`,
              overflowY: 'auto !important',
              borderRadius: `${r('card')} !important`,
            },
            '#termly-code-snippet-support .t-consentPrompt button': {
              minHeight: `${s('xl')} !important`,
            },
          },
          '@media (prefers-reduced-motion: reduce)': {
            '.flow-page-enter, .flow-section-enter, .flow-card-enter': { animation: 'none' },
          },
        }}
      />
      {children}
    </MuiThemeProvider>
  );
};
